import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, BACKEND_URL, formatDate, CountryOptions } from '../lib/utils';
import { useNavigationBlock } from '../context/NavigationBlockContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { CompanyPicker } from '../components/CompanyPicker';
import { CompanyMultiPicker } from '../components/CompanyMultiPicker';
import { fetchCompaniesByIds } from '../lib/companies';
import { fetchPersonsByIds, searchPersons } from '../lib/persons';
import { referenceCache } from '../lib/referenceCache';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar as CalendarIcon, 
  Loader2, 
  Plus, 
  X,
  Users,
  GraduationCap,
  Building,
  Search,
  ExternalLink,
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Printer,
  FileText,
  Upload,
  Image,
  Star,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';

// Certificate Print View Component - supports both default and template-based printing
const CertificatePrintView = ({ certificate, template, classData, onClose }) => {
  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      alert('Please allow popups for this site to print certificates');
      return;
    }
    
    // If we have a template with positions, use template-based printing
    if (template) {
      const orientation = template.page_orientation || 'portrait';
      const pageWidth = orientation === 'landscape' ? 297 : 210; // A4 in mm
      const pageHeight = orientation === 'landscape' ? 210 : 297;
      
      // Helper to generate positioned text
      const renderField = (fieldConfig, value) => {
        if (!fieldConfig || !fieldConfig.enabled || !value) return '';
        const { x, y, font_size, font_weight, text_align } = fieldConfig;
        return `
          <div style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            font-size: ${font_size || 12}pt;
            font-weight: ${font_weight || 'normal'};
            text-align: ${text_align || 'left'};
            ${text_align === 'center' ? 'transform: translateX(-50%);' : ''}
            ${text_align === 'right' ? 'transform: translateX(-100%);' : ''}
          ">${value}</div>
        `;
      };
      
      // Helper to generate positioned image (for signature)
      const renderImage = (fieldConfig, imageUrl) => {
        if (!fieldConfig || !fieldConfig.enabled || !imageUrl) return '';
        const { x, y, font_size } = fieldConfig;
        // font_size is repurposed as image height in mm for signature
        const height = font_size ? font_size * 2 : 30; // Default 30mm height
        return `
          <img src="${imageUrl}" style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            height: ${height}mm;
            object-fit: contain;
          " />
        `;
      };
      
      // Construct signature URL - signature_url already includes /api prefix
      const signatureFullUrl = classData?.signature_url 
        ? window.location.origin + classData.signature_url
        : null;
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificate - ${certificate.certificate_number}</title>
            <style>
              @page {
                size: ${orientation};
                margin: 0;
              }
              body {
                font-family: 'Inter', Arial, sans-serif;
                margin: 0;
                padding: 0;
              }
              .certificate-page {
                position: relative;
                width: ${pageWidth}mm;
                height: ${pageHeight}mm;
                overflow: hidden;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="certificate-page">
              ${renderField(template.certificate_number, certificate.certificate_number)}
              ${renderField(template.student_name, certificate.person_name)}
              ${renderField(template.course_name, certificate.outcome_name || certificate.course_name)}
              ${renderField(template.company_name, certificate.company_name)}
              ${renderField(template.issue_date, new Date(certificate.issue_date).toLocaleDateString('en-GB'))}
              ${renderField(template.expiry_date, certificate.expiry_date ? new Date(certificate.expiry_date).toLocaleDateString('en-GB') : null)}
              ${renderField(template.instructor_names, classData?.instructor_names)}
              ${renderField(template.instructor_numbers, classData?.instructor_numbers)}
              ${renderField(template.training_center_name, classData?.training_center_name)}
              ${renderField(template.duration_hours, classData?.duration_hours ? `${classData.duration_hours} hours` : null)}
              ${renderField(template.declaration_text, classData?.declaration_text)}
              ${renderField(template.extra_text, classData?.extra_text)}
              ${renderImage(template.signature, signatureFullUrl)}
            </div>
          </body>
        </html>
      `);
    } else {
      // Default print layout (no template)
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificate - ${certificate.certificate_number}</title>
            <style>
              body {
                font-family: 'Inter', sans-serif;
                padding: 40px;
                max-width: 800px;
                margin: 0 auto;
              }
              .certificate-header {
                text-align: center;
                margin-bottom: 40px;
                padding-bottom: 20px;
                border-bottom: 2px solid #2C9DE1;
              }
              .certificate-header img {
                max-height: 60px;
              }
              .certificate-field {
                margin: 20px 0;
                padding: 10px 0;
                border-bottom: 1px solid #e5e5e5;
              }
              .certificate-field label {
                font-weight: 600;
                color: #666;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .certificate-field p {
                font-size: 18px;
                margin: 5px 0 0 0;
                color: #111;
              }
              .certificate-number {
                font-family: monospace;
                font-size: 24px;
                color: #2C9DE1;
              }
              @media print {
                body { padding: 20px; }
              }
            </style>
          </head>
          <body>
            <div class="certificate-header">
              <img src="${window.location.origin}/logo.png" alt="EMP Medic" />
              <p style="color: #666; margin-top: 10px;">Training Certificate</p>
            </div>
            <div class="certificate-field">
              <label>Certificate Number</label>
              <p class="certificate-number">${certificate.certificate_number}</p>
            </div>
            <div class="certificate-field">
              <label>Participant Name</label>
              <p>${certificate.person_name}</p>
            </div>
            <div class="certificate-field">
              <label>Course</label>
              <p>${certificate.outcome_name || certificate.course_name}</p>
            </div>
            ${certificate.company_name ? `
            <div class="certificate-field">
              <label>Company</label>
              <p>${certificate.company_name}</p>
            </div>
            ` : ''}
            ${classData?.instructor_names ? `
            <div class="certificate-field">
              <label>Instructor(s)</label>
              <p>${classData.instructor_names}</p>
            </div>
            ` : ''}
            ${classData?.declaration_text ? `
            <div class="certificate-field">
              <label>Declaration</label>
              <p>${classData.declaration_text}</p>
            </div>
            ` : ''}
            ${classData?.extra_text ? `
            <div class="certificate-field">
              <label>Additional Information</label>
              <p>${classData.extra_text}</p>
            </div>
            ` : ''}
            <div style="display: flex; gap: 40px;">
              <div class="certificate-field" style="flex: 1;">
                <label>Issue Date</label>
                <p>${new Date(certificate.issue_date).toLocaleDateString('en-GB')}</p>
              </div>
              ${certificate.expiry_date ? `
              <div class="certificate-field" style="flex: 1;">
                <label>Expiry Date</label>
                <p>${new Date(certificate.expiry_date).toLocaleDateString('en-GB')}</p>
              </div>
              ` : ''}
            </div>
            ${classData?.signature_url ? `
            <div class="certificate-field" style="margin-top: 30px;">
              <label>Signature</label>
              <img src="${window.location.origin}${classData.signature_url}" style="max-height: 60px; margin-top: 10px;" />
            </div>
            ` : ''}
          </body>
        </html>
      `);
    }
    
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
    };
    
    // Fallback: try printing after a short delay if onload doesn't fire
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Certificate Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-1.5">
          <div className="border-b pb-1.5">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Certificate Number</label>
            <p className="text-base font-mono text-[#2C9DE1]">{certificate.certificate_number}</p>
          </div>

          <div className="border-b pb-1.5">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Participant Name</label>
            <p className="text-sm font-semibold text-slate-900">{certificate.person_name}</p>
          </div>

          <div className="border-b pb-1.5">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Course</label>
            <p className="text-sm text-slate-900">{certificate.outcome_name || certificate.course_name}</p>
          </div>

          {certificate.company_name && (
            <div className="border-b pb-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Company</label>
              <p className="text-sm text-slate-900">{certificate.company_name}</p>
            </div>
          )}

          {classData?.instructor_names && (
            <div className="border-b pb-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Instructor(s)</label>
              <p className="text-sm text-slate-900">{classData.instructor_names}</p>
              {classData?.instructor_numbers && (
                <p className="text-xs text-slate-500">#{classData.instructor_numbers}</p>
              )}
            </div>
          )}

          {classData?.declaration_text && (
            <div className="border-b pb-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Declaration</label>
              <p className="text-slate-700 text-xs">{classData.declaration_text}</p>
            </div>
          )}

          {classData?.extra_text && (
            <div className="border-b pb-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Extra Text</label>
              <p className="text-slate-700 text-xs">{classData.extra_text}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="border-b pb-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Issue Date</label>
              <p className="text-sm text-slate-900">{formatDate(certificate.issue_date)}</p>
            </div>

            <div className="border-b pb-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Expiry Date</label>
              <p className="text-sm text-slate-900">
                {certificate.expiry_date ? formatDate(certificate.expiry_date) : '-'}
              </p>
            </div>
          </div>

          {classData?.signature_url && (
            <div className="border-b pb-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Signature</label>
              <img 
                src={`${BACKEND_URL}${classData.signature_url}`}
                alt="Signature"
                className="mt-1 max-h-12 object-contain"
              />
            </div>
          )}

          {/* Template indicator */}
          {template && (
            <div className="p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-700 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Print template: <strong>{template.name}</strong> ({template.page_orientation})
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t mt-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button 
            size="sm"
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            onClick={handlePrint}
          >
            <Printer className="w-3 h-3 mr-1.5" />
            {template ? 'Print with Template' : 'Print'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ClassForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For editing existing class
  const isEditing = !!id;
  const { blockNavigation, unblockNavigation, safeNavigate } = useNavigationBlock();
  
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Track if form has unsaved changes
  const [isDirty, setIsDirty] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  
  // Certificates for this class (to show certificate numbers)
  const [classCertificates, setClassCertificates] = useState([]);
  
  // Certificate template for printing
  const [certificateTemplate, setCertificateTemplate] = useState(null);
  
  // New student dialog
  const [showNewStudentDialog, setShowNewStudentDialog] = useState(false);
  const [savingStudent, setSavingStudent] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [newStudentForm, setNewStudentForm] = useState({
    first_name: '',
    last_name: '',
    id_number: '',
    telephone: '',
    mobile: '',
    email: '',
    personal_email: '',
    position: '',
    country: '',
    city: '',
    company_ids: [],
    email_permission: true
  });
  
  // Student details dialog
  const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);
  
  // Certificate print dialog
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  
  // Remove student confirmation dialog
  const [removeStudentConfirm, setRemoveStudentConfirm] = useState(null);
  
  // Search/filter for students
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const studentSearchDebounce = React.useRef(null);
  
  // Raw date input values for typing
  const [dateStartText, setDateStartText] = useState('');
  const [dateCompletedText, setDateCompletedText] = useState('');
  
  // Company dialog for InHouse classes
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({
    name: '',
    email: '',
    telephone: '',
    country: 'Cyprus',
    city: '',
    category: ''
  });
  const [savingCompany, setSavingCompany] = useState(false);
  
  // Declarations
  const [declarations, setDeclarations] = useState([]);
  
  // Signature upload state
  const [uploadingSignature, setUploadingSignature] = useState(false);
  
  const [formData, setFormData] = useState({
    course_id: '',
    training_center_id: '',
    class_type: 'multi', // 'inhouse' or 'multi'
    company_id: '', // Only used when class_type is 'inhouse'
    duration_hours: 8,
    date_start: '',
    date_completed: '',
    country: 'Cyprus',
    city: '',
    location_name: '',
    student_ids: [],
    instructor_ids: [],
    primary_instructor_id: '',
    filing_number: '',  // Alphanumeric reference for physical document storage
    declaration_id: '',  // Optional declaration for certificate
    extra_text: '',  // Additional text for certificate
    signature_url: '',  // URL to signature image
    is_historical: false  // Mark class as historical import
  });
  
  // Store original class data when editing (to check certificates_issued status)
  const [existingClass, setExistingClass] = useState(null);
  const fetchedRef = React.useRef(null);

  useEffect(() => {
    // StrictMode mounts components twice in dev. Guard so we don't fire all
    // the API calls twice on every Edit-Class load.
    if (fetchedRef.current === id) return;
    fetchedRef.current = id;
    const fetchData = async () => {
      try {
        if (id) {
          // EDIT MODE: one aggregated round-trip — backend parallelizes the queries.
          const bundleRes = await axios.get(`${API_URL}/classes/${id}/edit-bundle`);
          const b = bundleRes.data;
          const coursesData = b.courses || [];
          setCourses(coursesData);
          setInstructors(b.instructors || []);
          setTrainingCenters(b.training_centers || []);
          setDeclarations(b.declarations || []);
          setStudents(b.enrolled_students || []);
          setCompanies(b.referenced_companies || []);
          if (b.certificate_template) setCertificateTemplate(b.certificate_template);
          if (b.certificates && b.certificates.length > 0) setClassCertificates(b.certificates);

          const cls = b.class;
          setExistingClass(cls);
          const initial = {
            course_id: cls.course_id,
            training_center_id: cls.training_center_id || '',
            class_type: cls.class_type || 'multi',
            company_id: cls.company_id || '',
            duration_hours: cls.duration_hours,
            date_start: cls.date_start,
            date_completed: cls.date_completed || '',
            country: cls.country || 'Cyprus',
            city: cls.city || '',
            location_name: cls.location_name || '',
            student_ids: cls.student_ids || [],
            instructor_ids: cls.instructor_ids || [],
            primary_instructor_id: cls.primary_instructor_id || '',
            filing_number: cls.filing_number || '',
            declaration_id: cls.declaration_id || '',
            extra_text: cls.extra_text || '',
            signature_url: cls.signature_url || '',
            is_historical: cls.is_historical || false,
          };
          setFormData(initial);
          setInitialFormData(initial);
          if (cls.date_start) setDateStartText(format(new Date(cls.date_start), 'dd/MM/yyyy'));
          if (cls.date_completed) setDateCompletedText(format(new Date(cls.date_completed), 'dd/MM/yyyy'));
        } else {
          // NEW CLASS: use the session reference cache (rarely changes within a session).
          const [courses, instructors, centers, declarations] = await Promise.all([
            referenceCache.courses(),
            referenceCache.instructors(),
            referenceCache.trainingCenters(),
            referenceCache.declarations(),
          ]);
          setCourses(courses);
          setInstructors(instructors);
          setTrainingCenters(centers);
          setDeclarations(declarations);
          setStudents([]);
          setCompanies([]);
          setInitialFormData({ ...formData });
        }
      } catch (error) {
        console.error('ClassForm load failed:', error);
        const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
        toast.error(`Failed to fetch data: ${detail}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Track if form has been modified
  useEffect(() => {
    if (!initialFormData) return;
    
    // Compare current form data with initial data
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setIsDirty(hasChanges);
    
    // Block/unblock navigation based on dirty state
    if (hasChanges) {
      blockNavigation('You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.');
    } else {
      unblockNavigation();
    }
  }, [formData, initialFormData, blockNavigation, unblockNavigation]);

  // Cleanup: unblock navigation when component unmounts
  useEffect(() => {
    return () => {
      unblockNavigation();
    };
  }, [unblockNavigation]);

  // Helper to get certificate number for a student
  const getCertificateNumber = (studentId) => {
    const cert = classCertificates.find(c => c.person_id === studentId);
    return cert ? cert.certificate_number : null;
  };

  // Helper to get full certificate object for a student
  const getStudentCertificate = (studentId) => {
    return classCertificates.find(c => c.person_id === studentId) || null;
  };

  // Re-sync certificates with the class's current course
  const [resyncingCerts, setResyncingCerts] = useState(false);
  const handleResyncCertificates = async () => {
    if (!window.confirm('Update all certificates of this class to match the current course name? Certificate numbers and dates will not change.')) return;
    setResyncingCerts(true);
    try {
      const res = await axios.post(`${API_URL}/classes/${id}/resync-certificates`);
      toast.success(res.data.message);
      if (res.data.certificates) setClassCertificates(res.data.certificates);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-sync certificates');
    } finally {
      setResyncingCerts(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.course_id) {
      toast.error('Please select a course');
      return;
    }
    
    if (!formData.training_center_id) {
      toast.error('Please select a training center');
      return;
    }
    
    if (!formData.date_completed) {
      toast.error('Please select a completion date');
      return;
    }

    setSaving(true);
    
    try {
      // Normalize empty primary_instructor_id to null so backend treats it as "no primary"
      const payload = {
        ...formData,
        primary_instructor_id: formData.primary_instructor_id || null
      };
      if (isEditing) {
        await axios.put(`${API_URL}/classes/${id}`, payload);
        toast.success('Class updated successfully');
        // Update initialFormData to current state after successful save
        setInitialFormData({...formData});
        setIsDirty(false);
        unblockNavigation();
        // Stay on edit page - don't navigate away
      } else {
        const response = await axios.post(`${API_URL}/classes`, payload);
        toast.success('Class created successfully');
        setIsDirty(false);
        unblockNavigation();
        // Navigate to edit page for the newly created class
        navigate(`/classes/${response.data.id}/edit`, { replace: true });
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save class';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewStudent = async (e) => {
    e.preventDefault();
    
    if (!newStudentForm.first_name || !newStudentForm.last_name || !newStudentForm.id_number) {
      toast.error('Please fill in required fields (First Name, Last Name, ID Number)');
      return;
    }
    
    setSavingStudent(true);
    
    try {
      const payload = {
        first_name: newStudentForm.first_name,
        last_name: newStudentForm.last_name,
        id_number: newStudentForm.id_number,
        telephone: newStudentForm.telephone || null,
        mobile: newStudentForm.mobile || null,
        email: newStudentForm.email || null,
        personal_email: newStudentForm.personal_email || null,
        position: newStudentForm.position || null,
        country: newStudentForm.country || null,
        city: newStudentForm.city || null,
        company_ids: newStudentForm.company_ids,
        training_center_ids: formData.training_center_id ? [formData.training_center_id] : [],
        email_permission: newStudentForm.email_permission
      };
      
      const response = await axios.post(`${API_URL}/persons`, payload);
      const newStudent = response.data;
      
      // Add to students list
      setStudents(prev => [...prev, newStudent]);
      
      // Auto-select the new student
      setFormData(prev => ({
        ...prev,
        student_ids: [...(prev.student_ids || []), newStudent.id]
      }));
      
      toast.success(`${newStudent.first_name} ${newStudent.last_name} added and selected`);
      
      // Reset form and close dialog
      setNewStudentForm({
        first_name: '',
        last_name: '',
        id_number: '',
        telephone: '',
        mobile: '',
        email: '',
        personal_email: '',
        position: '',
        country: '',
        city: '',
        company_ids: [],
        email_permission: true
      });
      setCompanySearch('');
      setShowNewStudentDialog(false);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to add student';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      toast.error(errorMessage);
    } finally {
      setSavingStudent(false);
    }
  };

  const toggleStudent = (studentId) => {
    const currentIds = formData.student_ids || [];
    const isRemoving = currentIds.includes(studentId);

    if (isRemoving) {
      // Show confirmation dialog before removing
      const student = students.find(s => s.id === studentId)
        || searchResults.find(s => s.id === studentId);
      setRemoveStudentConfirm({ studentId, student });
    } else {
      // Adding student — also cache its record locally so the Selected panel
      // can render its details even after the user clears the search box.
      const sourceRec = searchResults.find(s => s.id === studentId);
      if (sourceRec && !students.find(s => s.id === studentId)) {
        setStudents(prev => [...prev, sourceRec]);
      }
      setFormData(prev => ({
        ...prev,
        student_ids: [...(prev.student_ids || []), studentId]
      }));
    }
  };

  const confirmRemoveStudent = () => {
    if (removeStudentConfirm) {
      setFormData(prev => ({
        ...prev,
        student_ids: (prev.student_ids || []).filter(id => id !== removeStudentConfirm.studentId)
      }));
      setRemoveStudentConfirm(null);
    }
  };

  const toggleInstructor = (instructorId) => {
    setFormData(prev => {
      const currentIds = prev.instructor_ids || [];
      const isRemoving = currentIds.includes(instructorId);
      const newIds = isRemoving
        ? currentIds.filter(id => id !== instructorId)
        : [...currentIds, instructorId];
      // Clear primary if removed; auto-set as primary if it's the first instructor selected
      let newPrimary = prev.primary_instructor_id || '';
      if (isRemoving && newPrimary === instructorId) {
        newPrimary = '';
      }
      return {
        ...prev,
        instructor_ids: newIds,
        primary_instructor_id: newPrimary
      };
    });
  };

  const setPrimaryInstructor = (instructorId) => {
    setFormData(prev => {
      // Only allow if instructor is already selected
      if (!prev.instructor_ids?.includes(instructorId)) return prev;
      // Toggle off if it's already the primary
      return {
        ...prev,
        primary_instructor_id: prev.primary_instructor_id === instructorId ? '' : instructorId
      };
    });
  };

  // Debounced search for the Available Students panel.
  // Only fetches results when the user actually types something — avoids
  // dumping all 5000+ students into the DOM on page open.
  useEffect(() => {
    const term = studentSearch.trim();
    if (studentSearchDebounce.current) clearTimeout(studentSearchDebounce.current);
    if (!term) {
      setSearchResults([]);
      setSearchingStudents(false);
      return;
    }
    setSearchingStudents(true);
    studentSearchDebounce.current = setTimeout(async () => {
      const list = await searchPersons(term, 50);
      setSearchResults(list);
      setSearchingStudents(false);
      // resolve any company names we don't yet have for these results
      const newCompanyIds = new Set();
      list.forEach((p) => {
        (p.company_ids || []).forEach((cid) => cid && newCompanyIds.add(cid));
        if (p.company_id) newCompanyIds.add(p.company_id);
      });
      const unknown = Array.from(newCompanyIds).filter(
        (cid) => !companies.find((c) => c.id === cid)
      );
      if (unknown.length > 0) {
        const more = await fetchCompaniesByIds(unknown);
        if (more.length > 0) {
          setCompanies((prev) => {
            const seen = new Set(prev.map((c) => c.id));
            return [...prev, ...more.filter((c) => !seen.has(c.id))];
          });
        }
      }
    }, 250);
    return () => studentSearchDebounce.current && clearTimeout(studentSearchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentSearch]);

  // List of students rendered in the Available Students panel.
  // - When the user types, show server-side matches.
  // - When idle, show the currently-enrolled students (so the user can
  //   uncheck them) but nothing more — saves a 5000-row payload.
  const filteredStudents = React.useMemo(() => {
    const term = studentSearch.trim();
    if (term) return searchResults;
    return students.filter((s) => (formData.student_ids || []).includes(s.id));
  }, [studentSearch, searchResults, students, formData.student_ids]);

  // Filter instructors by selected training center AND selected course
  const filteredInstructors = instructors.filter(i => {
    // Must be assigned to the selected training center
    const matchesCenter = !formData.training_center_id || i.training_center_ids?.includes(formData.training_center_id);
    // Must be able to teach the selected course
    const matchesCourse = !formData.course_id || i.course_ids?.includes(formData.course_id);
    return matchesCenter && matchesCourse;
  });

  // Build a Map<id, name> once per companies change — O(1) lookups in tables.
  const companyMap = React.useMemo(() => {
    const m = new Map();
    companies.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [companies]);

  // Helper to get company names (handles both single ID and array)
  const getCompanyNames = (student) => {
    // Check for array first, then fall back to single ID
    const companyIds = student.company_ids || (student.company_id ? [student.company_id] : []);
    if (!companyIds || companyIds.length === 0) return '-';
    return companyIds.map(id => companyMap.get(id) || '').filter(Boolean).join(', ') || '-';
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto" data-testid="class-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => safeNavigate('/classes')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Classes
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {isEditing ? 'Edit Class' : 'Create New Class'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isEditing ? 'Update class details and participants' : 'Set up a new training class'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Training Center & Course Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-[#2C9DE1]" />
              Training Center & Course
            </CardTitle>
            <CardDescription>Select where and what will be taught</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="training_center">Training Center *</Label>
                <Select
                  value={formData.training_center_id}
                  onValueChange={(value) => {
                    // Check if currently selected course is available for the new training center
                    const currentCourse = courses.find(c => c.id === formData.course_id);
                    const courseStillValid = !currentCourse || 
                      !currentCourse.training_center_ids || 
                      currentCourse.training_center_ids.length === 0 ||
                      currentCourse.training_center_ids.includes(value);
                    
                    // Auto-populate default signature from training center (only when creating new class)
                    const selectedCenter = trainingCenters.find(tc => tc.id === value);
                    const shouldSetSignature = !isEditing && selectedCenter?.default_signature_url;
                    
                    setFormData(prev => ({ 
                      ...prev, 
                      training_center_id: value,
                      // Clear course if it's not available for the new training center
                      course_id: courseStillValid ? prev.course_id : '',
                      // Auto-populate signature only when creating new class
                      signature_url: shouldSetSignature ? selectedCenter.default_signature_url : prev.signature_url
                    }));
                    
                    if (shouldSetSignature) {
                      toast.info('Default signature applied from training center');
                    }
                    
                    // Clear certificate template if course was cleared
                    if (!courseStillValid) {
                      setCertificateTemplate(null);
                    }
                  }}
                >
                  <SelectTrigger data-testid="class-center-select">
                    <SelectValue placeholder="Select a training center" />
                  </SelectTrigger>
                  <SelectContent>
                    {trainingCenters.map((center) => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name} {center.city ? `(${center.city})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="course">Course *</Label>
                <Select
                  value={formData.course_id}
                  disabled={!formData.training_center_id}
                  onValueChange={async (value) => {
                    const selectedCourse = courses.find(c => c.id === value);
                    
                    setFormData(prev => ({ 
                      ...prev, 
                      course_id: value,
                      // Auto-populate default hours from course, but only if not editing
                      duration_hours: !isEditing && selectedCourse?.default_hours 
                        ? selectedCourse.default_hours 
                        : prev.duration_hours
                    }));
                    
                    // Fetch certificate template if course has one
                    if (selectedCourse?.certificate_template_id) {
                      try {
                        const templateRes = await axios.get(`${API_URL}/certificate-templates/${selectedCourse.certificate_template_id}`);
                        setCertificateTemplate(templateRes.data);
                      } catch (err) {
                        console.error('Failed to fetch certificate template:', err);
                        setCertificateTemplate(null);
                      }
                    } else {
                      setCertificateTemplate(null);
                    }
                  }}
                >
                  <SelectTrigger data-testid="class-course-select">
                    <SelectValue placeholder={formData.training_center_id ? "Select a course" : "Select a training center first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {courses
                      .filter(course => {
                        // If no training center selected, show all courses
                        if (!formData.training_center_id) return true;
                        // If course has no training_center_ids, it's available to all
                        if (!course.training_center_ids || course.training_center_ids.length === 0) return true;
                        // Otherwise, check if selected center is in the course's list
                        return course.training_center_ids.includes(formData.training_center_id);
                      })
                      .map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.name} ({course.default_hours || 8}h)
                          {course.certificate_template_id && ' 📄'}
                          {course.default_signature_url && ' ✍️'}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {!formData.training_center_id && (
                  <p className="text-xs text-amber-600">Please select a training center first to see available courses</p>
                )}
                {certificateTemplate && (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Print template: {certificateTemplate.name}
                  </p>
                )}
              </div>
            </div>

            {/* Class Type Selection */}
            <div className="space-y-3 pt-2 border-t">
              <Label>Class Type *</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="class_type"
                    value="multi"
                    checked={formData.class_type === 'multi'}
                    onChange={() => setFormData({ ...formData, class_type: 'multi', company_id: '' })}
                    className="w-4 h-4 text-[#2C9DE1]"
                  />
                  <span className="text-sm font-medium">Multi</span>
                  <span className="text-xs text-slate-500">(Multiple companies)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="class_type"
                    value="inhouse"
                    checked={formData.class_type === 'inhouse'}
                    onChange={() => setFormData({ ...formData, class_type: 'inhouse' })}
                    className="w-4 h-4 text-[#2C9DE1]"
                  />
                  <span className="text-sm font-medium">InHouse</span>
                  <span className="text-xs text-slate-500">(Single company)</span>
                </label>
              </div>
            </div>

            {/* Historical Class Checkbox */}
            <div className="flex items-center space-x-3 pt-2 border-t">
              <Checkbox
                id="is_historical"
                checked={formData.is_historical}
                onCheckedChange={(checked) => setFormData({ ...formData, is_historical: checked })}
                disabled={existingClass?.certificates_issued}
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="is_historical"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Historical Class
                </label>
                <p className="text-xs text-slate-500">
                  {existingClass?.certificates_issued 
                    ? 'Cannot change after certificates have been issued.'
                    : 'Mark this class as historical/imported. Allows entering custom certificate numbers when issuing certificates.'}
                </p>
              </div>
            </div>

            {/* Company Selection for InHouse */}
            {formData.class_type === 'inhouse' && (
              <div className="space-y-2 p-4 bg-slate-50 rounded-lg border">
                <Label>Company *</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <CompanyPicker
                      value={formData.company_id}
                      onChange={(value) => setFormData({ ...formData, company_id: value })}
                      selectedCompany={
                        formData.company_id
                          ? companies.find((c) => c.id === formData.company_id)
                          : null
                      }
                      placeholder="Search and select a company"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCompanyDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </div>
                {formData.company_id && (
                  <p className="text-xs text-slate-500">
                    Selected: {companies.find(c => c.id === formData.company_id)?.name || formData.company_id}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="duration">Duration (Hours) *</Label>
              <Input
                id="duration"
                type="number"
                min={1}
                value={formData.duration_hours}
                onChange={(e) => setFormData({ ...formData, duration_hours: parseInt(e.target.value) || 1 })}
                className="max-w-[200px]"
                data-testid="class-duration-input"
              />
              <p className="text-xs text-slate-500">Default hours from course can be changed for this specific class</p>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[#2C9DE1]" />
              Schedule
            </CardTitle>
            <CardDescription>Set class dates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={dateStartText}
                    onChange={(e) => setDateStartText(e.target.value)}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (!value) {
                        setFormData({ ...formData, date_start: '' });
                        return;
                      }
                      // Try to parse the date from various formats
                      const datePatterns = [
                        { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: 'dmy' },
                        { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, order: 'dmy' },
                        { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: 'ymd' },
                        { pattern: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, order: 'dmy' },
                      ];
                      
                      for (const { pattern, order } of datePatterns) {
                        const match = value.match(pattern);
                        if (match) {
                          let day, month, year;
                          if (order === 'ymd') {
                            [, year, month, day] = match;
                          } else {
                            [, day, month, year] = match;
                          }
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          if (!isNaN(date.getTime()) && date.getFullYear() > 1900) {
                            setFormData({ ...formData, date_start: date.toISOString() });
                            setDateStartText(format(date, 'dd/MM/yyyy'));
                            return;
                          }
                        }
                      }
                      // Invalid date - show error and revert
                      toast.error('Invalid date format. Use DD/MM/YYYY');
                      if (formData.date_start) {
                        setDateStartText(format(new Date(formData.date_start), 'dd/MM/yyyy'));
                      } else {
                        setDateStartText('');
                      }
                    }}
                    className="flex-1"
                    data-testid="class-startdate-input"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        data-testid="class-startdate-btn"
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.date_start ? new Date(formData.date_start) : undefined}
                        onSelect={(date) => {
                          setFormData({ ...formData, date_start: date ? date.toISOString() : '' });
                          setDateStartText(date ? format(date, 'dd/MM/yyyy') : '');
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Completion Date *</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={dateCompletedText}
                    onChange={(e) => setDateCompletedText(e.target.value)}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (!value) {
                        setFormData({ ...formData, date_completed: '' });
                        return;
                      }
                      // Try to parse the date from various formats
                      const datePatterns = [
                        { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: 'dmy' },
                        { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, order: 'dmy' },
                        { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: 'ymd' },
                        { pattern: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, order: 'dmy' },
                      ];
                      
                      for (const { pattern, order } of datePatterns) {
                        const match = value.match(pattern);
                        if (match) {
                          let day, month, year;
                          if (order === 'ymd') {
                            [, year, month, day] = match;
                          } else {
                            [, day, month, year] = match;
                          }
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          if (!isNaN(date.getTime()) && date.getFullYear() > 1900) {
                            setFormData({ ...formData, date_completed: date.toISOString() });
                            setDateCompletedText(format(date, 'dd/MM/yyyy'));
                            return;
                          }
                        }
                      }
                      // Invalid date - show error and revert
                      toast.error('Invalid date format. Use DD/MM/YYYY');
                      if (formData.date_completed) {
                        setDateCompletedText(format(new Date(formData.date_completed), 'dd/MM/yyyy'));
                      } else {
                        setDateCompletedText('');
                      }
                    }}
                    className="flex-1"
                    data-testid="class-enddate-input"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        data-testid="class-enddate-btn"
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.date_completed ? new Date(formData.date_completed) : undefined}
                        onSelect={(date) => {
                          setFormData({ ...formData, date_completed: date ? date.toISOString() : '' });
                          setDateCompletedText(date ? format(date, 'dd/MM/yyyy') : '');
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader>
            <CardTitle>Location Details</CardTitle>
            <CardDescription>Specific location within the training center</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location_name">Location/Room Name</Label>
                <Input
                  id="location_name"
                  value={formData.location_name}
                  onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                  placeholder="e.g., Training Room A"
                  data-testid="class-location-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  list="cyprus-cities"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder={formData.country === 'Cyprus' ? 'Select or type a city' : 'City'}
                  data-testid="class-city-input"
                />
                {formData.country === 'Cyprus' && (
                  <datalist id="cyprus-cities">
                    <option value="Nicosia" />
                    <option value="Limassol" />
                    <option value="Larnaca" />
                    <option value="Paphos" />
                    <option value="Famagusta" />
                  </datalist>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country || "none"}
                  onValueChange={(value) => setFormData({ ...formData, country: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="class-country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select country</SelectItem>
                    {CountryOptions.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Filing Number */}
            <div className="space-y-2 mt-4">
              <Label htmlFor="filing_number">Filing Number</Label>
              <Input
                id="filing_number"
                value={formData.filing_number}
                onChange={(e) => setFormData({ ...formData, filing_number: e.target.value })}
                placeholder="e.g., 2026-A-001"
                className="max-w-xs"
                data-testid="class-filing-number-input"
              />
              <p className="text-sm text-slate-500">Reference number for physical document storage location</p>
            </div>

            {/* Declaration */}
            <div className="space-y-2 mt-4">
              <Label htmlFor="declaration_id">Declaration (Optional)</Label>
              <Select
                value={formData.declaration_id}
                onValueChange={(value) => setFormData({ ...formData, declaration_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="max-w-md" data-testid="class-declaration-select">
                  <SelectValue placeholder="Select a declaration (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No declaration</SelectItem>
                  {declarations.map((declaration) => (
                    <SelectItem key={declaration.id} value={declaration.id}>
                      {declaration.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.declaration_id && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg border">
                  <p className="text-sm text-slate-600 italic">
                    "{declarations.find(d => d.id === formData.declaration_id)?.text}"
                  </p>
                </div>
              )}
              <p className="text-sm text-slate-500">
                A specific statement about what was covered in this class, to be printed on certificates
              </p>
            </div>

            {/* Extra Text */}
            <div className="space-y-2 mt-4">
              <Label htmlFor="extra_text">Extra Text (Optional)</Label>
              <Textarea
                id="extra_text"
                value={formData.extra_text}
                onChange={(e) => setFormData({ ...formData, extra_text: e.target.value })}
                placeholder="Additional text to print on certificates..."
                className="max-w-md min-h-[80px]"
                data-testid="class-extra-text-input"
              />
              <p className="text-sm text-slate-500">
                Custom text that can be printed on certificates if configured in the print template
              </p>
            </div>

            {/* Signature Upload */}
            <div className="space-y-2 mt-4">
              <Label>Signature Image (Optional)</Label>
              <div className="flex items-start gap-4">
                <div className="flex-1 max-w-md">
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      id="signature_upload"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        // Validate file size (max 2MB)
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error('File size must be less than 2MB');
                          return;
                        }
                        
                        setUploadingSignature(true);
                        const formDataUpload = new FormData();
                        formDataUpload.append('file', file);
                        
                        try {
                          const response = await axios.post(`${API_URL}/uploads/signature`, formDataUpload, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setFormData(prev => ({ ...prev, signature_url: response.data.path }));
                          toast.success('Signature uploaded successfully');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to upload signature');
                        } finally {
                          setUploadingSignature(false);
                          e.target.value = ''; // Reset input
                        }
                      }}
                      data-testid="signature-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('signature_upload').click()}
                      disabled={uploadingSignature}
                      className="flex items-center gap-2"
                    >
                      {uploadingSignature ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {formData.signature_url ? 'Change Signature' : 'Upload Signature'}
                    </Button>
                    {formData.signature_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setFormData({ ...formData, signature_url: '' })}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    Upload an instructor's signature image (PNG or JPG, max 2MB)
                  </p>
                </div>
                
                {/* Signature Preview */}
                {formData.signature_url && (
                  <div className="border rounded-lg p-3 bg-slate-50">
                    <p className="text-xs text-slate-500 mb-2">Preview:</p>
                    <img 
                      src={`${BACKEND_URL}${formData.signature_url}`}
                      alt="Signature preview"
                      className="max-h-16 max-w-[150px] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#2C9DE1]" />
              Instructors
            </CardTitle>
            <CardDescription>
              {formData.training_center_id && formData.course_id
                ? 'Select instructors who can teach this course at the selected training center. Click the star to mark one as primary (appears first in lists and on certificates).'
                : formData.training_center_id
                  ? 'Please select a course to see qualified instructors'
                  : 'Please select a training center and course first'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {formData.training_center_id && formData.course_id ? (
              filteredInstructors.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No instructors can teach this course at this training center</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => navigate('/instructors')}
                  >
                    Manage instructors
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    {filteredInstructors.map((instructor) => {
                      const isSelected = formData.instructor_ids?.includes(instructor.id);
                      const isPrimary = formData.primary_instructor_id === instructor.id;
                      return (
                      <div 
                        key={instructor.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-[#2C9DE1]/5 border-[#2C9DE1]'
                            : 'hover:bg-slate-50'
                        }`}
                        onClick={() => toggleInstructor(instructor.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected || false}
                              onCheckedChange={() => toggleInstructor(instructor.id)}
                            />
                          </div>
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {instructor.first_name} {instructor.last_name}
                              {isPrimary && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium" data-testid={`instructor-primary-badge-${instructor.id}`}>
                                  <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                                  Primary
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500">#{instructor.instructor_number}</p>
                          </div>
                        </div>
                        {isSelected && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPrimaryInstructor(instructor.id); }}
                            className={`p-2 rounded-md transition-colors ${
                              isPrimary
                                ? 'text-amber-500 hover:bg-amber-50'
                                : 'text-slate-400 hover:bg-slate-100 hover:text-amber-500'
                            }`}
                            title={isPrimary ? 'Click to remove as primary' : 'Set as primary instructor'}
                            data-testid={`instructor-primary-toggle-${instructor.id}`}
                          >
                            <Star className={`w-5 h-5 ${isPrimary ? 'fill-current' : ''}`} />
                          </button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  
                  {/* Read-only Instructor Numbers field */}
                  {formData.instructor_ids?.length > 0 && (
                    <div className="pt-3 border-t">
                      <Label className="text-sm text-slate-600">Selected Instructor Number(s)</Label>
                      <div className="mt-1 p-3 bg-slate-50 rounded-lg border">
                        <p className="font-mono text-slate-700" data-testid="instructor-numbers-display">
                          {instructors
                            .filter(i => formData.instructor_ids?.includes(i.id))
                            .map(i => i.instructor_number)
                            .join(', ')}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        This field will appear on printed certificates if configured in the print template
                      </p>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a training center and course to see qualified instructors</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Students */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#2C9DE1]" />
                  Students
                </CardTitle>
                <CardDescription>
                  Select students to enroll in this class ({formData.student_ids?.length || 0} selected)
                </CardDescription>
              </div>
              <Button
                type="button"
                onClick={() => setShowNewStudentDialog(true)}
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                data-testid="add-new-student-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Student
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Type a name, ID number, or email to search students..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="pl-10"
                data-testid="student-search-input"
              />
            </div>

            {/* Selected students badges */}
            {formData.student_ids?.length > 0 && (
              <div className="border rounded-lg bg-slate-50">
                <div className="px-3 py-2 border-b bg-emerald-50 flex items-center justify-between">
                  <span className="text-sm font-medium text-emerald-800">
                    Selected Students ({formData.student_ids.length})
                  </span>
                  {isEditing && classCertificates.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1.5 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                      onClick={handleResyncCertificates}
                      disabled={resyncingCerts}
                      title="If the class course was changed after certificates were issued, this updates the course name on all certificates"
                      data-testid="resync-certificates-btn"
                    >
                      {resyncingCerts ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      Re-sync certificates with course
                    </Button>
                  )}
                </div>
                <div className="max-h-[250px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">ID</TableHead>
                        <TableHead className="text-xs">Company</TableHead>
                        <TableHead className="text-xs">Email</TableHead>
                        <TableHead className="text-xs">Position</TableHead>
                        <TableHead className="text-xs">Certificate #</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.student_ids.map(id => {
                        const student = students.find(s => s.id === id);
                        if (!student) return null;
                        const certNumber = getCertificateNumber(id);
                        const certificate = getStudentCertificate(id);
                        return (
                          <TableRow key={id} className="bg-white hover:bg-red-50 group">
                            <TableCell className="font-medium py-2">
                              {student.first_name} {student.last_name}
                            </TableCell>
                            <TableCell className="font-mono text-xs py-2">
                              {student.id_number || '-'}
                            </TableCell>
                            <TableCell className="text-sm py-2">
                              {getCompanyNames(student) || '-'}
                            </TableCell>
                            <TableCell className="text-sm py-2">
                              {student.email || '-'}
                            </TableCell>
                            <TableCell className="text-sm py-2">
                              {student.position || '-'}
                            </TableCell>
                            <TableCell className="py-2">
                              {certNumber ? (
                                <Badge className="bg-emerald-100 text-emerald-800 font-mono text-xs">
                                  {certNumber}
                                </Badge>
                              ) : (
                                <span className="text-slate-400 text-xs">-</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2">
                              <div className="flex items-center gap-1">
                                {certificate && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 opacity-70 hover:opacity-100 hover:bg-blue-100 hover:text-blue-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedCertificate(certificate);
                                    }}
                                    title="Print Certificate"
                                    data-testid={`print-cert-${id}`}
                                  >
                                    <Printer className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 opacity-50 group-hover:opacity-100 hover:bg-red-100 hover:text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleStudent(id);
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Students table */}
            <div className="border rounded-lg max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>ID Number</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchingStudents ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        Searching...
                      </TableCell>
                    </TableRow>
                  ) : filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        {studentSearch
                          ? 'No students match your search'
                          : 'Type at least one character to search students, or add a new one above.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((student) => (
                      <TableRow 
                        key={student.id}
                        className={`${
                          formData.student_ids?.includes(student.id) ? 'bg-[#2C9DE1]/5' : 'hover:bg-slate-50'
                        }`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={formData.student_ids?.includes(student.id) || false}
                            onCheckedChange={() => toggleStudent(student.id)}
                          />
                        </TableCell>
                        <TableCell 
                          className="font-medium cursor-pointer hover:text-[#2C9DE1] hover:underline"
                          onClick={() => setSelectedStudentDetails(student)}
                        >
                          {student.first_name} {student.last_name}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{student.id_number}</TableCell>
                        <TableCell className="text-slate-600">{getCompanyNames(student)}</TableCell>
                        <TableCell>{student.email || '-'}</TableCell>
                        <TableCell>{student.position || '-'}</TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/students?edit=${student.id}`, '_blank')}
                            title="Edit student in new tab"
                          >
                            <ExternalLink className="w-4 h-4 text-slate-500 hover:text-[#2C9DE1]" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Submit buttons */}
        <div className="flex items-center justify-end gap-4 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => safeNavigate('/classes')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            disabled={saving}
            data-testid="class-submit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              isEditing ? 'Update Class' : 'Create Class'
            )}
          </Button>
        </div>
      </form>

      {/* New Student Dialog */}
      <Dialog open={showNewStudentDialog} onOpenChange={setShowNewStudentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Add New Student
            </DialogTitle>
            <DialogDescription>
              Add a new student to enroll in this class
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddNewStudent} className="space-y-4">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_first_name">First Name *</Label>
                  <Input
                    id="new_first_name"
                    value={newStudentForm.first_name}
                    onChange={(e) => setNewStudentForm({ ...newStudentForm, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="new-student-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_last_name">Last Name *</Label>
                  <Input
                    id="new_last_name"
                    value={newStudentForm.last_name}
                    onChange={(e) => setNewStudentForm({ ...newStudentForm, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="new-student-lastname"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_id_number">ID Number *</Label>
                <Input
                  id="new_id_number"
                  value={newStudentForm.id_number}
                  onChange={(e) => setNewStudentForm({ ...newStudentForm, id_number: e.target.value })}
                  placeholder="CY12345678"
                  data-testid="new-student-idnumber"
                />
                <p className="text-xs text-slate-500">This will be used as the initial password</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_position">Position</Label>
                <Input
                  id="new_position"
                  value={newStudentForm.position}
                  onChange={(e) => setNewStudentForm({ ...newStudentForm, position: e.target.value })}
                  placeholder="e.g., Safety Officer, Nurse, Manager"
                  data-testid="new-student-position"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_email">Work Email</Label>
                  <Input
                    id="new_email"
                    type="email"
                    value={newStudentForm.email}
                    onChange={(e) => setNewStudentForm({ ...newStudentForm, email: e.target.value })}
                    placeholder="john@company.com"
                    data-testid="new-student-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_personal_email">Personal Email</Label>
                  <Input
                    id="new_personal_email"
                    type="email"
                    value={newStudentForm.personal_email}
                    onChange={(e) => setNewStudentForm({ ...newStudentForm, personal_email: e.target.value })}
                    placeholder="john@personal.com"
                    data-testid="new-student-personalemail"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_telephone">Telephone</Label>
                  <Input
                    id="new_telephone"
                    value={newStudentForm.telephone}
                    onChange={(e) => setNewStudentForm({ ...newStudentForm, telephone: e.target.value })}
                    placeholder="+357 22 123456"
                    data-testid="new-student-telephone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_mobile">Mobile</Label>
                  <Input
                    id="new_mobile"
                    value={newStudentForm.mobile}
                    onChange={(e) => setNewStudentForm({ ...newStudentForm, mobile: e.target.value })}
                    placeholder="+357 99 123456"
                    data-testid="new-student-mobile"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Location</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_city">City</Label>
                  <Input
                    id="new_city"
                    value={newStudentForm.city}
                    onChange={(e) => setNewStudentForm({ ...newStudentForm, city: e.target.value })}
                    placeholder="Nicosia"
                    data-testid="new-student-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_country">Country</Label>
                  <Select
                    value={newStudentForm.country || "none"}
                    onValueChange={(value) => setNewStudentForm({ ...newStudentForm, country: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="new-student-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select country</SelectItem>
                      {CountryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Company */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Company Assignment</h3>
              <div className="space-y-2">
                <Label>Companies</Label>
                <CompanyMultiPicker
                  value={newStudentForm.company_ids}
                  onChange={(ids) => setNewStudentForm({ ...newStudentForm, company_ids: ids })}
                  selectedCompanies={newStudentForm.company_ids
                    .map((id) => companies.find((c) => c.id === id))
                    .filter(Boolean)}
                />
              </div>
            </div>

            {/* Preferences */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="new_email_permission"
                checked={newStudentForm.email_permission}
                onCheckedChange={(checked) => setNewStudentForm({ ...newStudentForm, email_permission: checked })}
                data-testid="new-student-emailpermission"
              />
              <Label htmlFor="new_email_permission" className="text-sm font-normal">
                Permission to send promotional emails
              </Label>
            </div>

            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowNewStudentDialog(false);
                  setCompanySearch('');
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={savingStudent}
                data-testid="new-student-submit-btn"
              >
                {savingStudent ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add & Select Student'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Student Details Dialog */}
      <Dialog open={!!selectedStudentDetails} onOpenChange={(open) => !open && setSelectedStudentDetails(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-slate-600" />
              </div>
              {selectedStudentDetails?.first_name} {selectedStudentDetails?.last_name}
            </DialogTitle>
            <DialogDescription className="font-mono">
              ID: {selectedStudentDetails?.id_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedStudentDetails && (
            <div className="space-y-4">
              {selectedStudentDetails.position && (
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Position:</span>
                  <span className="font-medium">{selectedStudentDetails.position}</span>
                </div>
              )}
              
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Contact Information</h4>
                {selectedStudentDetails.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Work Email:</span>
                    <span>{selectedStudentDetails.email}</span>
                  </div>
                )}
                {selectedStudentDetails.personal_email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Personal Email:</span>
                    <span>{selectedStudentDetails.personal_email}</span>
                  </div>
                )}
                {selectedStudentDetails.telephone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Telephone:</span>
                    <span>{selectedStudentDetails.telephone}</span>
                  </div>
                )}
                {selectedStudentDetails.mobile && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Mobile:</span>
                    <span>{selectedStudentDetails.mobile}</span>
                  </div>
                )}
                {!selectedStudentDetails.email && !selectedStudentDetails.personal_email && !selectedStudentDetails.telephone && !selectedStudentDetails.mobile && (
                  <p className="text-sm text-slate-500">No contact information available</p>
                )}
              </div>
              
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Location</h4>
                {(selectedStudentDetails.city || selectedStudentDetails.country) ? (
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{[selectedStudentDetails.city, selectedStudentDetails.country].filter(Boolean).join(', ')}</span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No location information</p>
                )}
              </div>
              
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Company</h4>
                {getCompanyNames(selectedStudentDetails) !== '-' ? (
                  <div className="flex items-center gap-3 text-sm">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span>{getCompanyNames(selectedStudentDetails)}</span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No company assigned</p>
                )}
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Email Permission:</span>
                  <Badge variant={selectedStudentDetails.email_permission ? "default" : "secondary"}>
                    {selectedStudentDetails.email_permission ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button 
              variant="outline"
              onClick={() => setSelectedStudentDetails(null)}
            >
              Close
            </Button>
            <Button 
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              onClick={() => {
                window.open(`/students?edit=${selectedStudentDetails?.id}`, '_blank');
                setSelectedStudentDetails(null);
              }}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Edit Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create New Company
            </DialogTitle>
            <DialogDescription>
              Add a new company for this InHouse class
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!newCompanyForm.name) {
              toast.error('Company name is required');
              return;
            }
            setSavingCompany(true);
            try {
              // Clean up empty strings to avoid validation errors
              const companyData = {
                name: newCompanyForm.name,
                ...(newCompanyForm.email && { email: newCompanyForm.email }),
                ...(newCompanyForm.telephone && { telephone: newCompanyForm.telephone }),
                ...(newCompanyForm.country && { country: newCompanyForm.country }),
                ...(newCompanyForm.city && { city: newCompanyForm.city }),
                ...(newCompanyForm.category && { category: newCompanyForm.category }),
              };
              const response = await axios.post(`${API_URL}/companies`, companyData);
              const newCompany = response.data;
              setCompanies([...companies, newCompany]);
              setFormData({ ...formData, company_id: newCompany.id });
              setCompanyDialogOpen(false);
              setNewCompanyForm({
                name: '',
                email: '',
                telephone: '',
                country: 'Cyprus',
                city: '',
                category: ''
              });
              toast.success('Company created and selected');
            } catch (error) {
              console.error('Company creation error:', error);
              const errorMsg = error.response?.data?.detail;
              toast.error(Array.isArray(errorMsg) ? errorMsg[0]?.msg : errorMsg || 'Failed to create company');
            } finally {
              setSavingCompany(false);
            }
          }}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={newCompanyForm.name}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, name: e.target.value })}
                  placeholder="Enter company name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_email">Email</Label>
                  <Input
                    id="company_email"
                    type="email"
                    value={newCompanyForm.email}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, email: e.target.value })}
                    placeholder="company@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_telephone">Telephone</Label>
                  <Input
                    id="company_telephone"
                    value={newCompanyForm.telephone}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, telephone: e.target.value })}
                    placeholder="+357..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_country">Country</Label>
                  <Select
                    value={newCompanyForm.country}
                    onValueChange={(value) => setNewCompanyForm({ ...newCompanyForm, country: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {CountryOptions.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_city">City</Label>
                  <Input
                    id="company_city"
                    value={newCompanyForm.city}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company_category">Category</Label>
                <Select
                  value={newCompanyForm.category}
                  onValueChange={(value) => setNewCompanyForm({ ...newCompanyForm, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hotel">Hotel</SelectItem>
                    <SelectItem value="Bank">Bank</SelectItem>
                    <SelectItem value="Gym">Gym</SelectItem>
                    <SelectItem value="Bakery">Bakery</SelectItem>
                    <SelectItem value="Kindergarten">Kindergarten</SelectItem>
                    <SelectItem value="Restaurant">Restaurant</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Office">Office</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setCompanyDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={savingCompany}
              >
                {savingCompany ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create & Select'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Certificate Print Dialog */}
      {selectedCertificate && (
        <CertificatePrintView 
          certificate={selectedCertificate}
          template={certificateTemplate}
          classData={(() => {
            // Pick primary (or first) instructor for certificate display
            const selected = instructors.filter(i => formData.instructor_ids?.includes(i.id));
            const primary = (formData.primary_instructor_id && selected.find(i => i.id === formData.primary_instructor_id)) || selected[0];
            return {
              instructor_names: primary ? `${primary.first_name} ${primary.last_name}` : '',
              instructor_numbers: primary?.instructor_number || '',
              training_center_name: trainingCenters.find(tc => tc.id === formData.training_center_id)?.name,
              duration_hours: formData.duration_hours,
              declaration_text: declarations.find(d => d.id === formData.declaration_id)?.text,
              extra_text: formData.extra_text,
              signature_url: formData.signature_url
            };
          })()}
          onClose={() => setSelectedCertificate(null)} 
        />
      )}

      {/* Remove Student Confirmation Dialog */}
      {removeStudentConfirm && (
        <Dialog open={true} onOpenChange={() => setRemoveStudentConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Remove Student
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to remove <strong>{removeStudentConfirm.student?.first_name} {removeStudentConfirm.student?.last_name}</strong> from this class?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setRemoveStudentConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmRemoveStudent}
              >
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ClassForm;
